import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Link2, ShoppingCart, AlertTriangle, Copy, Check, Paperclip } from 'lucide-react';
import {
  getIncomingQuote,
  createShareLink,
  getShareLinks,
  getAttachments,
  getAttachmentDownloadUrl,
  listQuotes,
  createQuote,
  addLineItems,
  ApiError,
} from '../api/client.js';
import type { IncomingQuoteDetail, ShareLink, QuoteAttachment } from '../api/client.js';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function IncomingQuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<IncomingQuoteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  // Share link dialog
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareRecipientName, setShareRecipientName] = useState('');
  const [shareRecipientEmail, setShareRecipientEmail] = useState('');
  const [shareExpiry, setShareExpiry] = useState('');
  const [shareCreating, setShareCreating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);

  // Attachments
  const [attachments, setAttachments] = useState<QuoteAttachment[]>([]);

  // Add to My Quote
  const [showAddToQuoteDialog, setShowAddToQuoteDialog] = useState(false);
  const [addTarget, setAddTarget] = useState<'existing' | 'new'>('new');
  const [draftQuotes, setDraftQuotes] = useState<Array<{ id: string; displayId: string | null; projectName: string | null }>>([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [addingItems, setAddingItems] = useState(false);
  const [addSuccess, setAddSuccess] = useState('');

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        const result = await getIncomingQuote(id);
        setData(result);
        // Load share links + attachments for this quote
        const linksResult = await getShareLinks(result.quote.id);
        setShareLinks(linksResult.links);
        getAttachments(result.quote.id).then((d) => setAttachments(d.attachments)).catch(() => {});
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to load quote');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleDownloadPdf = async () => {
    if (!id) return;
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/sharing/incoming/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Quote-${data?.quote.displayId || id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download PDF');
    }
    setPdfLoading(false);
  };

  const handleCreateShareLink = async () => {
    if (!data) return;
    setShareCreating(true);
    try {
      const result = await createShareLink({
        quoteId: data.quote.id,
        recipientName: shareRecipientName || undefined,
        recipientEmail: shareRecipientEmail || undefined,
        expiresInDays: shareExpiry ? parseInt(shareExpiry, 10) : undefined,
      });
      setGeneratedUrl(result.url);
      setShareLinks((prev) => [result.link, ...prev]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create share link');
    }
    setShareCreating(false);
  };

  const handleCopyLink = async (url: string) => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Load draft quotes when Add to My Quote dialog opens
  useEffect(() => {
    if (showAddToQuoteDialog) {
      listQuotes({ status: 'draft' }).then((d) => {
        setDraftQuotes(d.quotes);
        if (d.quotes.length > 0) {
          setSelectedQuoteId(d.quotes[0]!.id);
          setAddTarget('existing');
        }
      }).catch(() => {});
    }
  }, [showAddToQuoteDialog]);

  const handleAddToQuote = async () => {
    if (!data) return;
    setAddingItems(true);
    const { sharedQuote: sq, quote: q, sourceTenant: st } = data;

    try {
      let targetId = selectedQuoteId;

      if (addTarget === 'new') {
        const projectName = sq.projectReference || q.projectName || 'Imported Quote';
        const result = await createQuote({
          projectName,
          notes: `Items imported from ${st.companyName || st.name} quote ${q.displayId || ''}`.trim(),
        });
        targetId = result.quote.id;
      }

      const itemsToAdd = q.lineItems
        .filter((item) => !item.isDescriptionRow)
        .map((item) => ({
          descriptionText: [item.partNumber, item.descriptionText || item.description]
            .filter(Boolean)
            .join(' \u2014 '),
          quantity: item.quantity || 1,
          unitPrice: parseFloat(item.unitPrice ?? '0'),
        }));

      if (itemsToAdd.length === 0) {
        setError('No priced items to add');
        setAddingItems(false);
        return;
      }

      await addLineItems(targetId, itemsToAdd);

      const targetQuote = addTarget === 'new'
        ? 'a new quote'
        : `quote ${draftQuotes.find((dq) => dq.id === targetId)?.displayId || targetId}`;

      setAddSuccess(`${itemsToAdd.length} items added to ${targetQuote}. You can now open the quote to adjust margins and pricing.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to add items');
    }
    setAddingItems(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="animate-fade-in">
        <button
          onClick={() => navigate('/incoming-quotes')}
          className="mb-4 inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Incoming Quotes
        </button>
        {error && (
          <div className="rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
        )}
      </div>
    );
  }

  const { sharedQuote, quote, sourceTenant } = data;
  const isExpired = quote.validUntil && new Date(quote.validUntil) < new Date();

  // Calculate totals from line items
  let totalAmount = 0;
  for (const item of quote.lineItems) {
    if (!item.isDescriptionRow) {
      const unitPrice = parseFloat(item.unitPrice ?? '0');
      const qty = item.quantity ?? 1;
      totalAmount += unitPrice * qty;
    }
  }

  return (
    <div className="animate-fade-in">
      <button
        onClick={() => navigate('/incoming-quotes')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Incoming Quotes
      </button>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      )}

      <div className="mx-auto max-w-[1100px]">
        {/* Header Card */}
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          {/* Source company info */}
          <div className="mb-4 flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500">Quote from</p>
              <h1 className="text-xl font-bold text-gray-100">
                {sourceTenant.companyName || sourceTenant.name}
              </h1>
              {sourceTenant.companyPhone && (
                <p className="text-sm text-gray-400">{sourceTenant.companyPhone}</p>
              )}
              {sourceTenant.companyEmail && (
                <p className="text-sm text-gray-400">{sourceTenant.companyEmail}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-brand-400">{quote.displayId || 'Quote'}</p>
              <p className="text-sm text-gray-500">Sent {formatDate(sharedQuote.sentAt)}</p>
            </div>
          </div>

          {/* Quote metadata */}
          <div className="mb-4 flex flex-wrap gap-6 text-sm">
            {(sharedQuote.projectReference || quote.projectName) && (
              <div>
                <span className="text-gray-500">Project: </span>
                <span className="text-gray-200">
                  {sharedQuote.projectReference || quote.projectName}
                </span>
              </div>
            )}
            {quote.validUntil && (
              <div>
                <span className="text-gray-500">Valid Until: </span>
                <span className={isExpired ? 'text-red-400' : 'text-gray-200'}>
                  {formatDate(quote.validUntil)}
                  {isExpired && ' (expired)'}
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-500">Items: </span>
              <span className="text-gray-200">
                {quote.lineItems.filter((i) => !i.isDescriptionRow).length}
              </span>
            </div>
          </div>

          {/* Expired warning */}
          {isExpired && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-800 bg-amber-900/20 px-4 py-2 text-sm text-amber-400">
              <AlertTriangle className="h-4 w-4" />
              This quote expired on {formatDate(quote.validUntil)}. Contact the manufacturer for
              updated pricing.
            </div>
          )}

          {/* Manufacturer notes */}
          {sharedQuote.notes && (
            <div className="mb-4 rounded-lg border border-gray-700 bg-gray-800 px-4 py-3">
              <p className="mb-1 text-xs font-medium text-gray-500">
                Note from {sourceTenant.companyName || sourceTenant.name}
              </p>
              <p className="whitespace-pre-wrap text-sm text-gray-300">{sharedQuote.notes}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              {pdfLoading ? 'Generating PDF...' : 'Download PDF'}
            </button>
            <button
              onClick={() => {
                setShowShareDialog(true);
                setGeneratedUrl('');
                setShareRecipientName('');
                setShareRecipientEmail('');
                setShareExpiry('');
              }}
              className="flex items-center gap-2 rounded-lg border border-gray-600 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800"
            >
              <Link2 className="h-4 w-4" />
              Share Link
            </button>
            <button
              onClick={() => { setShowAddToQuoteDialog(true); setAddSuccess(''); }}
              className="flex items-center gap-2 rounded-lg border border-gray-600 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800"
            >
              <ShoppingCart className="h-4 w-4" />
              Add to My Quote
            </button>
          </div>

          {/* Share link analytics */}
          {shareLinks.length > 0 && (
            <div className="mt-4 border-t border-gray-800 pt-3">
              <p className="mb-2 text-xs font-medium text-gray-500">Share Links</p>
              <div className="space-y-1">
                {shareLinks.map((link) => (
                  <div key={link.id} className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">
                      {link.recipientName || link.recipientEmail || 'Anonymous'}
                      {link.expiresAt && (
                        <span className="ml-1 text-gray-600">
                          (expires {formatDate(link.expiresAt)})
                        </span>
                      )}
                    </span>
                    <span className="text-gray-500">
                      {link.viewCount} views &middot; {link.downloadCount} downloads
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Line Items Table */}
        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-800">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-800/50 text-left text-xs uppercase tracking-wider text-gray-400">
                <th className="px-4 py-3 text-center" style={{ width: 40 }}>#</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3 text-center" style={{ width: 60 }}>Qty</th>
                <th className="px-4 py-3 text-right" style={{ width: 110 }}>Unit Price</th>
                <th className="px-4 py-3 text-right" style={{ width: 120 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {quote.lineItems.map((item, idx) => {
                if (item.isDescriptionRow) {
                  return (
                    <tr key={item.id} className="bg-gray-900/50">
                      <td className="px-4 py-2 text-center text-xs text-gray-600">{idx + 1}</td>
                      <td
                        colSpan={4}
                        className="px-4 py-2 text-sm italic text-gray-500"
                      >
                        {item.descriptionText}
                      </td>
                    </tr>
                  );
                }

                const unitPrice = parseFloat(item.unitPrice ?? '0');
                const qty = item.quantity ?? 1;
                const lineTotal = unitPrice * qty;

                return (
                  <tr key={item.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                    <td className="px-4 py-3 text-center text-sm text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-200">
                        {item.partNumber || item.descriptionText || '\u2014'}
                      </div>
                      {item.partNumber && item.descriptionText && (
                        <div className="text-xs text-gray-500">{item.descriptionText}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-300">{qty}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-300">
                      {formatCurrency(unitPrice)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-100">
                      {formatCurrency(lineTotal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-700">
                <td colSpan={4} className="px-4 py-3 text-right text-sm font-semibold text-gray-300">
                  Total:
                </td>
                <td className="px-4 py-3 text-right text-base font-bold text-gray-100">
                  {formatCurrency(totalAmount)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Boilerplate */}
        {quote.boilerplateText && (
          <div className="mt-4 rounded-lg border border-gray-800 bg-gray-900 px-4 py-3">
            <p className="whitespace-pre-wrap text-xs text-gray-500">{quote.boilerplateText}</p>
          </div>
        )}

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="mt-4 rounded-lg border border-gray-800 bg-gray-900 p-4">
            <h3 className="mb-2 text-sm font-medium text-gray-300">
              <Paperclip className="mr-1 inline h-4 w-4" />
              Attachments
            </h3>
            <div className="space-y-2">
              {attachments.map((att) => (
                <a
                  key={att.id}
                  href={getAttachmentDownloadUrl(att.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300"
                >
                  <Paperclip className="h-3.5 w-3.5 text-gray-500" />
                  {att.label || att.originalFilename}
                  <span className="text-xs text-gray-500">({formatFileSize(att.fileSize)})</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Share Link Dialog */}
      {showShareDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-100">Create Share Link</h3>
            <p className="mt-1 text-sm text-gray-400">
              Generate a link anyone can use to view this quote. No login required.
            </p>

            {!generatedUrl ? (
              <>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="mb-1 block text-sm text-gray-400">
                      Recipient Name (optional)
                    </label>
                    <input
                      type="text"
                      value={shareRecipientName}
                      onChange={(e) => setShareRecipientName(e.target.value)}
                      placeholder="e.g., John Smith"
                      className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-400">
                      Recipient Email (optional)
                    </label>
                    <input
                      type="email"
                      value={shareRecipientEmail}
                      onChange={(e) => setShareRecipientEmail(e.target.value)}
                      placeholder="e.g., john@company.com"
                      className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-400">Expires in (optional)</label>
                    <select
                      value={shareExpiry}
                      onChange={(e) => setShareExpiry(e.target.value)}
                      className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    >
                      <option value="">Never</option>
                      <option value="7">7 days</option>
                      <option value="14">14 days</option>
                      <option value="30">30 days</option>
                      <option value="90">90 days</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setShowShareDialog(false)}
                    className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateShareLink}
                    disabled={shareCreating}
                    className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {shareCreating ? 'Creating...' : 'Create Link'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mt-4 rounded-lg bg-gray-800 p-3">
                  <p className="mb-1 text-xs text-gray-500">Share this link:</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={generatedUrl}
                      className="flex-1 rounded bg-gray-900 px-2 py-1.5 text-sm text-gray-200 focus:outline-none"
                    />
                    <button
                      onClick={() => handleCopyLink(generatedUrl)}
                      className="flex items-center gap-1 rounded px-2 py-1.5 text-xs text-brand-400 hover:bg-gray-700"
                    >
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setShowShareDialog(false)}
                    className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add to My Quote Dialog */}
      {showAddToQuoteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-lg border border-gray-800 bg-gray-900 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-100">Add Items to Your Quote</h3>
            <p className="mt-1 text-sm text-gray-400">
              Pull {quote.lineItems.filter((i) => !i.isDescriptionRow).length} line items from this
              manufacturer quote into your own quote. The manufacturer&apos;s pricing will become your cost basis.
            </p>

            <div className="mt-4 space-y-3">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="addTarget"
                  checked={addTarget === 'new'}
                  onChange={() => setAddTarget('new')}
                  className="text-brand-500"
                />
                <span className="text-sm text-gray-300">Create a new quote</span>
              </label>

              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="addTarget"
                  checked={addTarget === 'existing'}
                  onChange={() => setAddTarget('existing')}
                  disabled={draftQuotes.length === 0}
                  className="text-brand-500"
                />
                <span className={`text-sm ${draftQuotes.length === 0 ? 'text-gray-600' : 'text-gray-300'}`}>
                  Add to existing draft quote
                  {draftQuotes.length === 0 && ' (no drafts available)'}
                </span>
              </label>

              {addTarget === 'existing' && draftQuotes.length > 0 && (
                <select
                  value={selectedQuoteId}
                  onChange={(e) => setSelectedQuoteId(e.target.value)}
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100"
                >
                  {draftQuotes.map((dq) => (
                    <option key={dq.id} value={dq.id}>
                      {dq.displayId || 'No ID'} &mdash; {dq.projectName || 'Untitled'}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="mt-4 rounded-lg bg-gray-800/50 p-3 text-xs text-gray-500">
              The manufacturer&apos;s unit prices will be set as your line item prices.
              You can then apply your margin and pricing adjustments in the quote builder.
            </div>

            {addSuccess && (
              <div className="mt-3 rounded-lg bg-green-900/30 px-3 py-2 text-sm text-green-400">
                {addSuccess}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => { setShowAddToQuoteDialog(false); setAddSuccess(''); }}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                {addSuccess ? 'Close' : 'Cancel'}
              </button>
              {!addSuccess && (
                <button
                  onClick={handleAddToQuote}
                  disabled={addingItems || (addTarget === 'existing' && !selectedQuoteId)}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {addingItems ? 'Adding Items...' : 'Add Items'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
